import type { ConcentratedLiquidityPositions } from "@stepfinance/liquidity";
import type { FC } from "react";
import { createContext, useContext, useMemo } from "react";

import { useCremaPools } from "../hooks/concentratedLiquidity/useCremaPools";
import { useOrcaWhirlpoolsQuery } from "../hooks/queries/liquidity/useOrcaWhirlpools";
import { useRaydiumClmmPoolsQuery } from "../hooks/queries/liquidity/useRaydiumClmmPools";

export interface ConcentratedLiquidityState {
  totalValue: number;
  positions: ConcentratedLiquidityPositions;
  totalPendingRewards: number;
  expected24HrRewards: number;
}

const initialState = {
  totalValue: 0,
  positions: [],
  totalPendingRewards: 0,
  expected24HrRewards: 0,
};

const ConcentratedLiquidityContext = createContext<ConcentratedLiquidityState>(initialState);

export const ConcentratedLiquidityContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { data: orcaWhirlPoolPositions } = useOrcaWhirlpoolsQuery();
  const { data: raydiumClmmPoolPositions } = useRaydiumClmmPoolsQuery();
  const cremaPoolPositions = useCremaPools();

  const concentratedLiquidityData = useMemo(() => {
    const positions = [...(orcaWhirlPoolPositions ?? []), ...(raydiumClmmPoolPositions ?? []), ...cremaPoolPositions];
    let totalValue = 0;
    for (const position of positions) {
      totalValue += position.valueInUSD ?? 0;
    }
    return { totalValue, positions };
  }, [orcaWhirlPoolPositions, raydiumClmmPoolPositions, cremaPoolPositions]);

  const totalPendingRewards = useMemo(() => {
    return concentratedLiquidityData.positions.reduce((total, position) => {
      return (
        total +
        position.rewards.reduce((value, r) => {
          return value + (r.rewardInUSD ?? 0);
        }, 0)
      );
    }, 0);
  }, [concentratedLiquidityData]);

  const expected24HrRewards = useMemo(() => {
    return concentratedLiquidityData.positions.reduce((total, position) => {
      const apr = position.apr;
      const totalValue = position.valueInUSD ?? 0;
      const expectedReward24hr = (apr / 365) * totalValue;
      return (total += expectedReward24hr);
    }, 0);
  }, [concentratedLiquidityData]);

  const contextValue = useMemo(
    () => ({
      ...concentratedLiquidityData,
      totalPendingRewards,
      expected24HrRewards,
    }),
    [concentratedLiquidityData, expected24HrRewards, totalPendingRewards],
  );

  return <ConcentratedLiquidityContext.Provider value={contextValue}>{children}</ConcentratedLiquidityContext.Provider>;
};

export function useConcentratedLiquidityContext(): ConcentratedLiquidityState {
  return useContext(ConcentratedLiquidityContext);
}
